import React, { useRef, useState, useCallback, useEffect } from "react";
import Sidebar from "./Sidebar";
import { useMap } from "./useMap";
import "./InteractiveMap.css";
import { useTranslation } from "react-i18next";

const PANELS = [
  { key: "search",   iconClass: "esri-icon-search",      label: "ძებნა" },
  { key: "layers",   iconClass: "esri-icon-layers",       label: "ლეიერები" },
  { key: "basemaps", iconClass: "esri-icon-basemap",      label: "ფონური რუქა" },
  { key: "measure",  iconClass: "esri-icon-measure-line", label: "გაზომვა" },
  { key: "print",    iconClass: "esri-icon-printer",      label: "ამოპრინტვა" },
];

const InteractiveMap = () => {
  const mapDiv = useRef(null);
  const [activePanel, setActivePanel] = useState("layers");
  // Persist dark-mode preference across page navigations via localStorage.
  // First visit defaults to light mode (false).
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try { return localStorage.getItem("mapDarkMode") === "true"; } catch { return false; }
  });

  const { layersStructure, setLayersStructure, measurementRef, homeRef, locateRef, searchResults, performSearch, navigateToSearchResult, isLightLayer, viewRef, setInfoMode } = useMap(mapDiv);
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [excludeKadastri, setExcludeKadastri] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [infoActive, setInfoActive] = useState(true);
  const basemapSynced = useRef(false);

  useEffect(() => {
    if (!searchTerm || searchTerm.length < 2) {
      setIsSearching(false);
      const timer = setTimeout(() => performSearch(searchTerm, excludeKadastri), 0);
      return () => clearTimeout(timer);
    }
    setIsSearching(true);
    const timer = setTimeout(async () => {
      await performSearch(searchTerm, excludeKadastri);
      setIsSearching(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, excludeKadastri, performSearch]);

  // Sync dark mode with basemap selection (dark basemap → dark mode ON)
  useEffect(() => {
    if (!basemapSynced.current) { basemapSynced.current = true; return; }
    setIsDarkMode(!isLightLayer);
  }, [isLightLayer]);

  // Shift ArcGIS built-in widgets (zoom, home, locate) to stay visible beside the panel.
  // action bar=50px + panel=300px + 5px gap = 355 when open, 55 when closed.
  useEffect(() => {
    const view = viewRef?.current;
    if (!view) return;
    const isMobile = window.innerWidth < 768;
    view.ui.padding = {
      top: isMobile ? 15 : 65,
      // On mobile the map container starts at x=50 (action bar is outside it),
      // so no extra left offset is needed for SDK widgets.
      left: activePanel ? 355 : 55,
      right: 15,
      bottom: 30,
    };
  }, [activePanel, viewRef]);

  // Persist preference + toggle Calcite dark mode class on <html>
  useEffect(() => {
    document.documentElement.classList.toggle("calcite-mode-dark", isDarkMode);
    try { localStorage.setItem("mapDarkMode", String(isDarkMode)); } catch (_) {}
  }, [isDarkMode]);

  const togglePanel = useCallback(
    (key) => setActivePanel((prev) => (prev === key ? null : key)),
    []
  );

  const handleToggle = (layer) => {
    layer.visible = !layer.visible;
    if (layer.visible && layer.layer) layer.layer.visible = true;
    setLayersStructure([...layersStructure]);
  };

  const handleDistance = () => { if (measurementRef?.current) measurementRef.current.activeTool = "distance"; };
  const handleArea     = () => { if (measurementRef?.current) measurementRef.current.activeTool = "area"; };
  const handleClear    = () => { measurementRef?.current?.clear(); };

  const handleInfoToggle = useCallback(() => {
    setInfoActive(prev => {
      const next = !prev;
      setInfoMode(next);
      return next;
    });
  }, [setInfoMode]);

  return (
    <div className="im-root" style={{ position: "relative", width: "100vw", height: "100vh", overflow: "hidden" }}>

      {/* ── Map fills the entire root ── */}
      <div ref={mapDiv} className={`im-map${infoActive ? " im-map--info" : ""}`} />

      {/* ── Custom zoom / home / locate buttons (replaces SDK Calcite buttons) ── */}
      <div
        className="im-nav-controls"
        style={{ left: activePanel ? 360 : 60 }}
      >
        <button className="im-nav-btn" title={t("გადიდება")}  onClick={() => { if (viewRef.current) viewRef.current.zoom += 1; }}>+</button>
        <button className="im-nav-btn" title={t("შემცირება")} onClick={() => { if (viewRef.current) viewRef.current.zoom -= 1; }}>−</button>
        <button className="im-nav-btn" title={t("საწყისი ხედი")} onClick={() => homeRef.current?.go()}>⌂</button>
        <button className="im-nav-btn" title={t("ჩემი მდებარეობა")} onClick={() => locateRef.current?.locate()}>◎</button>
      </div>

      {/* ── Action bar — always visible, position absolute left:0 ── */}
      <div className="im-action-bar">
          {PANELS.map(({ key, iconClass, label }) => (
            <button
              key={key}
              className={`im-action${activePanel === key ? " im-action--active" : ""}`}
              title={t(label)}
              onClick={() => togglePanel(key)}
            >
              <span className={`esri-icon ${iconClass}`} />
            </button>
          ))}

          {/* Info / identify toggle — enables popup-on-click when active */}
          <button
            className={`im-action${infoActive ? " im-action--active" : ""}`}
            title={infoActive ? t("ინფო რეჟიმი ჩართულია") : t("ობიექტის ინფო")}
            onClick={handleInfoToggle}
          >
            <span className="esri-icon esri-icon-description" />
          </button>

          {/* Spacer pushes dark-mode toggle to the bottom */}
          <div className="im-action-bar__spacer" />

          <button
            className={`im-action im-action--dark-toggle${isDarkMode ? " im-action--active" : ""}`}
            title={isDarkMode ? t("ღია რეჟიმი") : t("ბნელი რეჟიმი")}
            onClick={() => {
              const next = !isDarkMode;
              setIsDarkMode(next);
              const view = viewRef?.current;
              if (view) {
                // dark-gray-vector: dark city canvas — close to satellite style but fully dark
                // topo-vector: standard light topographic for light mode
                view.map.basemap = next ? "dark-gray-vector" : "hybrid";
              }
            }}
          >
            {isDarkMode ? "☀" : "🌙"}
          </button>
        </div>

        {/* Sliding panel — collapses to width 0 when no panel active */}
        <div className={`im-panel${activePanel ? " im-panel--open" : ""}`}>

          {/* ── Search ── */}
          <div className={`im-section${activePanel === "search" ? " im-section--active" : ""}`}>
            <div className="im-section__hdr">
              <span>{t("ძებნა")}</span>
              <button className="im-close" onClick={() => setActivePanel(null)}>✕</button>
            </div>
            <div className="im-source-select-wrap">
              <select
                className="im-source-select"
                value={excludeKadastri ? "exclude" : "include"}
                onChange={e => setExcludeKadastri(e.target.value === "exclude")}
              >
                <option value="include">{t("საკადასტრო მონაცემებით")}</option>
                <option value="exclude">{t("საკადასტრო მონაცემების გარეშე")}</option>
              </select>
            </div>
            <div className="im-search-input-wrap">
              <input
                className="im-search-input"
                type="text"
                placeholder={t("ობიექტის ძებნა...")}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button className="im-search-clear" onClick={() => setSearchTerm("")}>✕</button>
              )}
            </div>
            <div className="im-search-results-panel">
              {isSearching ? (
                <div className="im-search-loader">
                  <div /><div /><div />
                </div>
              ) : (
                searchResults.map((group, gi) => (
                  <div key={gi} className="im-search-group">
                    <div className="im-search-group__title">{group.sourceName}</div>
                    {group.features.map((feature, fi) => (
                      <div
                        key={fi}
                        className="im-search-item"
                        onClick={() => navigateToSearchResult({ feature, customData: group.customData, layer: group.layer })}
                      >
                        {feature.attributes[group.displayField] ?? "—"}
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* ── Layers ── */}
          <div className={`im-section${activePanel === "layers" ? " im-section--active" : ""}`}>
            <div className="im-section__hdr">
              <span>{t("ლეიერები")}</span>
              <button className="im-close" onClick={() => setActivePanel(null)}>✕</button>
            </div>
            <div className="im-section__body im-section__body--scroll">
              <Sidebar layers={layersStructure} onToggle={handleToggle} isLight={isLightLayer} />
            </div>
          </div>

          {/* ── Basemaps ── */}
          <div className={`im-section${activePanel === "basemaps" ? " im-section--active" : ""}`}>
            <div className="im-section__hdr">
              <span>{t("ფონური რუქა")}</span>
              <button className="im-close" onClick={() => setActivePanel(null)}>✕</button>
            </div>
            <div id="basemapGalleryDiv" className="im-section__body im-section__body--scroll" />
          </div>

          {/* ── Measurement ── */}
          <div className={`im-section${activePanel === "measure" ? " im-section--active" : ""}`}>
            <div className="im-section__hdr">
              <span>{t("გაზომვა")}</span>
              <button className="im-close" onClick={() => setActivePanel(null)}>✕</button>
            </div>
            <div className="im-section__body">
              <div className="im-measure-tools">
                <button
                  className="esri-widget--button esri-interactive esri-icon-measure-line im-measure-btn"
                  title={t("მანძილის მზომი")}
                  onClick={handleDistance}
                />
                <button
                  className="esri-widget--button esri-interactive esri-icon-measure-area im-measure-btn"
                  title={t("ფართობის მზომი")}
                  onClick={handleArea}
                />
                <button
                  className="esri-widget--button esri-interactive esri-icon-trash im-measure-btn"
                  title={t("გასუფთავება")}
                  onClick={handleClear}
                />
              </div>
            </div>
          </div>

          {/* ── Print ── */}
          <div className={`im-section${activePanel === "print" ? " im-section--active" : ""}`}>
            <div className="im-section__hdr">
              <span>{t("ამოპრინტვა")}</span>
              <button className="im-close" onClick={() => setActivePanel(null)}>✕</button>
            </div>
            <div id="printExpandDiv" className="im-section__body im-section__body--scroll" />
          </div>

        </div>

    </div>
  );
};

export default InteractiveMap;
